import React, { useState, useEffect } from 'react';
import { useContent } from '../ContentContext';
import { useAuth } from '../context/AuthContext';
import { UPLOAD_MEDIA_URL, UPDATE_CONTENT_URL } from '../config';
import { 
    PencilSquareIcon, 
    TrashIcon, 
    PlusIcon, 
    CheckIcon, 
    XMarkIcon,
    ArrowRightOnRectangleIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    DocumentTextIcon,
} from '@heroicons/react/24/outline';
import ImageUploader from './ImageUploader';
import IconPicker from './IconPicker';
import RichTextEditor from './RichTextEditor';

// Section layout metadata for the picker UI
const SECTION_TYPES = [
    { value: 'hero',         label: 'Hero Banner',    desc: 'Full-width image with title overlay' },
    { value: 'text',         label: 'Text Block',     desc: 'Centered title + body text' },
    { value: 'image_left',   label: 'Image Left',     desc: 'Image left, text right' },
    { value: 'image_right',  label: 'Image Right',    desc: 'Text left, image right' },
    { value: 'image_full',   label: 'Full-bg Image',  desc: 'Full-width background image with text overlay' },
    { value: 'cards',        label: 'Cards Grid',     desc: 'Grid of content cards (JSON body)' },
    { value: 'two_col',      label: 'Two Columns',    desc: 'Two equal text columns (split body with |||)' },
    { value: 'gallery_grid', label: 'Gallery Grid',   desc: 'Image grid (comma-separated URLs)' },
] as const;

type SectionTypeValue = typeof SECTION_TYPES[number]['value'];

// ── Pages Tab Component ──────────────────────────────────────────────────────

interface PagesTabProps {
    pages: any[];
    navArray: any[];
    isAdding: boolean;
    setIsAdding: (v: boolean) => void;
    newItemForm: any;
    setNewItemForm: (v: any) => void;
    handleAction: (action: string, data: any) => void;
}

const EMPTY_SECTION = {
    section_type: 'text' as SectionTypeValue,
    title: '',
    subtitle: '',
    body: '',
    image_url: '',
    button_label: '',
    button_link: '',
    badge_text: '',
    highlight_word: '',
    bg_color: '#ffffff',
    sort_order: 0,
};

// ── BG Color Picker ───────────────────────────────────────────────────────────
// Preset swatches + a native color input for any custom hex
const PRESET_COLORS = [
    { hex: '#ffffff', label: 'White' },
    { hex: '#f9fafb', label: 'Light Gray' },
    { hex: '#f3f4f6', label: 'Gray 100' },
    { hex: '#0A1D2D', label: 'Dark Navy' },
    { hex: '#EF5D22', label: 'Brand Orange' },
    { hex: '#1e40af', label: 'Blue' },
    { hex: '#166534', label: 'Green' },
    { hex: '#7c3aed', label: 'Purple' },
    { hex: '#be123c', label: 'Red' },
    { hex: '#000000', label: 'Black' },
];

const BgColorPicker = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
    const safeVal = value?.startsWith('#') ? value : '#ffffff';
    return (
        <div>
            <label className="text-xs font-bold text-gray-500 uppercase block mb-2">Background Colour</label>
            <div className="flex items-center flex-wrap gap-2">
                {PRESET_COLORS.map(c => (
                    <button
                        key={c.hex}
                        type="button"
                        title={c.label}
                        onClick={() => onChange(c.hex)}
                        className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 ${safeVal === c.hex ? 'border-[#EF5D22] scale-110 ring-2 ring-[#EF5D22]/40' : 'border-gray-300'}`}
                        style={{ backgroundColor: c.hex }}
                    />
                ))}
                {/* Custom colour input */}
                <label className="relative w-8 h-8 rounded-full overflow-hidden border-2 border-dashed border-gray-400 cursor-pointer hover:scale-110 transition-all" title="Custom colour">
                    <span className="absolute inset-0 flex items-center justify-center text-gray-400 text-xs font-bold pointer-events-none">+</span>
                    <input
                        type="color"
                        value={safeVal}
                        onChange={e => onChange(e.target.value)}
                        className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                    />
                </label>
                {/* Show current value */}
                <div className="flex items-center gap-1.5 ml-1">
                    <div className="w-6 h-6 rounded border border-gray-300 flex-shrink-0" style={{ backgroundColor: safeVal }} />
                    <span className="text-xs font-mono text-gray-500">{safeVal}</span>
                </div>
            </div>
        </div>
    );
};

// ── TwoColEditor — split rich HTML editor for two_col layout ─────────────────
const TwoColEditor: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
    const sep = '|||';
    const parts = value.split(sep);
    const col1 = parts[0] || '';
    const col2 = parts[1] || '';
    return (
        <div className="space-y-3">
            <div>
                <label className="text-xs font-semibold text-gray-400 block mb-1">Column 1</label>
                <RichTextEditor
                    value={col1}
                    onChange={v => onChange(v + sep + col2)}
                    placeholder="Left column content…"
                    minHeight={120}
                />
            </div>
            <div>
                <label className="text-xs font-semibold text-gray-400 block mb-1">Column 2</label>
                <RichTextEditor
                    value={col2}
                    onChange={v => onChange(col1 + sep + v)}
                    placeholder="Right column content…"
                    minHeight={120}
                />
            </div>
        </div>
    );
};

// ── Section Form Fields ───────────────────────────────────────────────────────
// Defined OUTSIDE PagesTab so it never re-mounts on state changes (fixes focus loss)
interface SectionFormFieldsProps {
    form: any;
    setForm: (v: any) => void;
}

const SectionFormFields: React.FC<SectionFormFieldsProps> = ({ form, setForm }) => {
    const needsImage = ['hero','image_left','image_right','image_full','gallery_grid'].includes(form.section_type);
    return (
        <div className="space-y-4">
            {/* Layout picker */}
            <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-2">Layout Type</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {SECTION_TYPES.map(t => (
                        <button
                            key={t.value}
                            type="button"
                            onClick={() => setForm({ ...form, section_type: t.value })}
                            className={`p-3 rounded-xl border-2 text-left transition-all ${form.section_type === t.value ? 'border-[#EF5D22] bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}
                        >
                            <p className={`text-xs font-bold ${form.section_type === t.value ? 'text-[#EF5D22]' : 'text-gray-700'}`}>{t.label}</p>
                            <p className="text-[10px] text-gray-400 mt-0.5">{t.desc}</p>
                        </button>
                    ))}
                </div>
            </div>

            {/* Title + highlight */}

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title</label>
                    <RichTextEditor
                        value={form.title || ''}
                        onChange={v => setForm({ ...form, title: v })}
                        placeholder="Section heading"
                        minHeight={40}
                    />
                </div>
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Highlight Word (in title)</label>
                    <input value={form.highlight_word || ''} onChange={e => setForm({ ...form, highlight_word: e.target.value })} placeholder="Word to colour orange" className="w-full p-2 border rounded-lg text-sm" />
                </div>
            </div>

            {/* Badge + subtitle */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Badge / Tag Text</label>
                    <RichTextEditor
                        value={form.badge_text || ''}
                        onChange={v => setForm({ ...form, badge_text: v })}
                        placeholder="e.g. About Us"
                        minHeight={30}
                    />
                </div>
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Subtitle</label>
                    <RichTextEditor
                        value={form.subtitle || ''}
                        onChange={v => setForm({ ...form, subtitle: v })}
                        placeholder="Optional sub-heading"
                        minHeight={30}
                    />
                </div>
            </div>

            {/* Body */}
            <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">
                    Body / Content
                    {form.section_type === 'cards'   && <span className="normal-case text-gray-400 ml-1">— paste JSON: [{`{"title":"...","text":"..."}`}, …]</span>}
                    {form.section_type === 'two_col' && <span className="normal-case text-gray-400 ml-1">— use the <code className="bg-gray-100 px-1">|||</code> separator between columns (HTML supported in each column)</span>}
                </label>
                {form.section_type === 'cards' ? (
                    <textarea
                        value={form.body || ''}
                        onChange={e => setForm({ ...form, body: e.target.value })}
                        placeholder='[{"title":"Card Title","text":"Card description"},…]'
                        className="w-full p-2 border rounded-lg text-sm font-mono"
                        rows={5}
                    />
                ) : form.section_type === 'two_col' ? (
                    <TwoColEditor value={form.body || ''} onChange={v => setForm({ ...form, body: v })} />
                ) : (
                    <RichTextEditor
                        value={form.body || ''}
                        onChange={v => setForm({ ...form, body: v })}
                        placeholder="Type the section body content…"
                    />
                )}
            </div>

            {/* Image uploader */}
            {needsImage && (
                <div>
                    {form.section_type === 'gallery_grid' ? (
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Image URLs (comma-separated)</label>
                            <textarea value={form.image_url || ''} onChange={e => setForm({ ...form, image_url: e.target.value })} placeholder="https://..., https://..." className="w-full p-2 border rounded-lg text-sm" rows={3} />
                        </div>
                    ) : (
                        <ImageUploader
                            label="Section Image"
                            folder="general"
                            value={form.image_url || ''}
                            onChange={url => setForm({ ...form, image_url: url })}
                        />
                    )}
                </div>
            )}

            {/* Button */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Button Label</label>
                    <input value={form.button_label || ''} onChange={e => setForm({ ...form, button_label: e.target.value })} placeholder="e.g. Learn More" className="w-full p-2 border rounded-lg text-sm" />
                </div>
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Button Link</label>
                    <input value={form.button_link || ''} onChange={e => setForm({ ...form, button_link: e.target.value })} placeholder="e.g. /contact" className="w-full p-2 border rounded-lg text-sm" />
                </div>
            </div>

            {/* Background colour picker */}
            <BgColorPicker value={form.bg_color || '#ffffff'} onChange={hex => setForm({ ...form, bg_color: hex })} />

            {/* Sort order */}
            <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Sort Order</label>
                <input type="number" value={form.sort_order || 0} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) })} className="w-24 p-2 border rounded-lg text-sm" />
            </div>
        </div>
    );
};

const PagesTab: React.FC<PagesTabProps> = ({
    pages, navArray, isAdding, setIsAdding, newItemForm, setNewItemForm, handleAction
}) => {
    // Which page is currently open (expanded)
    const [openPageId, setOpenPageId] = useState<number | null>(null);
    // Section being added to the open page
    const [addingSection, setAddingSection] = useState(false);
    const [sectionForm, setSectionForm] = useState<any>({ ...EMPTY_SECTION });
    // Section being edited
    const [editingSection, setEditingSection] = useState<any>(null);

    const openPage = pages.find(p => p.id === openPageId);

    const resetSection = () => {
        setSectionForm({ ...EMPTY_SECTION });
        setAddingSection(false);
        setEditingSection(null);
    };

    const saveSection = (pageId: number) => {
        if (editingSection) {
            handleAction('edit_section', { ...editingSection });
        } else {
            handleAction('add_section', { page_id: pageId, ...sectionForm });
        }
        resetSection();
    };

    const moveSection = (section: any, dir: 'up' | 'down', siblings: any[]) => {
        const idx = siblings.findIndex(s => s.id === section.id);
        const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= siblings.length) return;
        const reordered = [...siblings];
        [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
        handleAction('reorder_sections', {
            page_id: section.page_id,
            order: reordered.map((s, i) => ({ id: s.id, sort_order: i + 1 }))
        });
    };

    return (
        <div className="space-y-6">
            {/* Add new page form */}
            {isAdding && (
                <div className="bg-gray-50 p-6 rounded-xl border-2 border-[#EF5D22]/20 mb-6">
                    <h3 className="font-bold text-lg mb-4">Create New Page</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Link to Menu Item</label>
                            <select
                                value={newItemForm.menu_id || ''}
                                onChange={e => {
                                    const item = navArray.find((n: any) => n.id === parseInt(e.target.value));
                                    const autoSlug = item ? item.link.replace(/^\//, '') : '';
                                    setNewItemForm({ ...newItemForm, menu_id: e.target.value, slug: autoSlug });
                                }}
                                className="w-full p-2 border rounded-lg"
                            >
                                <option value="">— Select menu item —</option>
                                {navArray.map((n: any) => (
                                    <option key={n.id} value={n.id}>{n.title} ({n.link})</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Slug (URL path)</label>
                            <input
                                value={newItemForm.slug || ''}
                                onChange={e => setNewItemForm({ ...newItemForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                                placeholder="e.g. about"
                                className="w-full p-2 border rounded-lg"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Sort Order</label>
                            <input
                                type="number"
                                value={newItemForm.sort_order || ''}
                                onChange={e => setNewItemForm({ ...newItemForm, sort_order: e.target.value })}
                                className="w-full p-2 border rounded-lg"
                            />
                        </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                        <button onClick={() => handleAction('add_page', newItemForm)} className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-1 text-sm font-bold">
                            <CheckIcon className="w-4 h-4" /> Create Page
                        </button>
                        <button onClick={() => setIsAdding(false)} className="bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-1 text-sm">
                            <XMarkIcon className="w-4 h-4" /> Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Page list */}
            {pages.length === 0 && !isAdding && (
                <div className="text-center py-20 text-gray-400">
                    <DocumentTextIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">No pages yet. Click <strong>Add New</strong> to create one.</p>
                </div>
            )}

            {pages.map((page: any) => (
                <div key={page.id} className="border rounded-2xl overflow-hidden">
                    {/* Page header row */}
                    <div className="flex items-center justify-between p-5 bg-white hover:bg-gray-50 cursor-pointer" onClick={() => setOpenPageId(openPageId === page.id ? null : page.id)}>
                        <div className="flex items-center gap-3">
                            <DocumentTextIcon className="w-5 h-5 text-[#EF5D22]" />
                            <div>
                                <p className="font-bold text-gray-900">{page.menu_title}</p>
                                <p className="text-xs text-gray-500">/{page.slug} · {(page.sections || []).length} section{(page.sections || []).length !== 1 ? 's' : ''}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                title="Delete page"
                                onClick={e => { e.stopPropagation(); if (window.confirm(`Delete page "${page.menu_title}" and all its sections?`)) handleAction('delete_page', { id: page.id }); }}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                            >
                                <TrashIcon className="w-4 h-4" />
                            </button>
                            <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${openPageId === page.id ? 'rotate-180' : ''}`} />
                        </div>
                    </div>

                    {/* Expanded section editor */}
                    {openPageId === page.id && (
                        <div className="border-t bg-gray-50 p-6 space-y-4">
                            {/* Existing sections */}
                            {(page.sections || []).sort((a: any, b: any) => a.sort_order - b.sort_order).map((sec: any, idx: number) => (
                                <div key={sec.id} className="bg-white border rounded-xl p-4">
                                    {editingSection?.id === sec.id ? (
                                        <div>
                                            <h4 className="font-bold mb-3 text-sm text-gray-700">Editing Section</h4>
                                            <SectionFormFields form={editingSection} setForm={setEditingSection} />
                                            <div className="mt-4 flex gap-2">
                                                <button onClick={() => saveSection(page.id)} className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-1 text-sm font-bold">
                                                    <CheckIcon className="w-4 h-4" /> Save Changes
                                                </button>
                                                <button onClick={resetSection} className="bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-1 text-sm">
                                                    <XMarkIcon className="w-4 h-4" /> Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <span className="text-xs font-mono bg-orange-100 text-[#EF5D22] px-2 py-0.5 rounded">{SECTION_TYPES.find(t => t.value === sec.section_type)?.label || sec.section_type}</span>
                                                <div>
                                                    <p className="font-semibold text-gray-800 text-sm">{sec.title || <span className="text-gray-400 italic">No title</span>}</p>
                                                    {sec.subtitle && <p className="text-xs text-gray-400">{sec.subtitle}</p>}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <button title="Move up" onClick={() => moveSection(sec, 'up', page.sections)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg" disabled={idx === 0}>
                                                    <ChevronUpIcon className="w-4 h-4" />
                                                </button>
                                                <button title="Move down" onClick={() => moveSection(sec, 'down', page.sections)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg" disabled={idx === (page.sections || []).length - 1}>
                                                    <ChevronDownIcon className="w-4 h-4" />
                                                </button>
                                                <button title="Edit section" onClick={() => { setEditingSection({ ...sec }); setAddingSection(false); }} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg">
                                                    <PencilSquareIcon className="w-4 h-4" />
                                                </button>
                                                <button title="Delete section" onClick={() => { if (window.confirm('Delete this section?')) handleAction('delete_section', { id: sec.id }); }} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg">
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Add section form */}
                            {addingSection ? (
                                <div className="bg-white border-2 border-[#EF5D22]/30 rounded-xl p-5">
                                    <h4 className="font-bold mb-4 text-sm text-gray-700">Add New Section</h4>
                                    <SectionFormFields form={sectionForm} setForm={setSectionForm} />
                                    <div className="mt-4 flex gap-2">
                                        <button onClick={() => saveSection(page.id)} className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-1 text-sm font-bold">
                                            <CheckIcon className="w-4 h-4" /> Add Section
                                        </button>
                                        <button onClick={resetSection} className="bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-1 text-sm">
                                            <XMarkIcon className="w-4 h-4" /> Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    onClick={() => { setAddingSection(true); setEditingSection(null); setSectionForm({ ...EMPTY_SECTION, sort_order: (page.sections || []).length + 1 }); }}
                                    className="w-full border-2 border-dashed border-gray-300 hover:border-[#EF5D22] text-gray-400 hover:text-[#EF5D22] py-4 rounded-xl flex items-center justify-center gap-2 font-medium transition-all"
                                >
                                    <PlusIcon className="w-5 h-5" />
                                    Add Section
                                </button>
                            )}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

// ── AddFormFieldPanel — add a new contact form field ────────────────────────
const AddFormFieldPanel: React.FC<{ onAdd: (f: any) => void }> = ({ onAdd }) => {
    const [open, setOpen] = React.useState(false);
    const [f, setF] = React.useState({ field_type: 'text', label: '', placeholder: '', options: '', is_required: 0, col_span: 1, sort_order: 99 });
    if (!open) {
        return (
            <button onClick={() => setOpen(true)} className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-[#EF5D22] hover:text-[#EF5D22] font-semibold transition-colors flex items-center justify-center gap-2">
                <PlusIcon className="w-5 h-5" /> Add Form Field
            </button>
        );
    }
    return (
        <div className="border-2 border-[#EF5D22]/30 rounded-xl p-5 bg-orange-50 space-y-4">
            <h4 className="font-bold text-[#EF5D22]">New Form Field</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                    <label className="text-xs text-gray-500 uppercase font-bold block mb-1">Type</label>
                    <select value={f.field_type} onChange={e => setF({...f, field_type: e.target.value})} className="w-full p-2 border rounded-lg text-sm">
                        <option value="text">Text</option>
                        <option value="tel">Phone</option>
                        <option value="email">Email</option>
                        <option value="textarea">Textarea</option>
                        <option value="select">Dropdown</option>
                    </select>
                </div>
                <div>
                    <label className="text-xs text-gray-500 uppercase font-bold block mb-1">Label *</label>
                    <input value={f.label} onChange={e => setF({...f, label: e.target.value})} placeholder="e.g. Full Name" className="w-full p-2 border rounded-lg text-sm" />
                </div>
                <div>
                    <label className="text-xs text-gray-500 uppercase font-bold block mb-1">Placeholder</label>
                    <input value={f.placeholder} onChange={e => setF({...f, placeholder: e.target.value})} placeholder="e.g. Enter your name" className="w-full p-2 border rounded-lg text-sm" />
                </div>
                <div>
                    <label className="text-xs text-gray-500 uppercase font-bold block mb-1">Width</label>
                    <select value={String(f.col_span)} onChange={e => setF({...f, col_span: parseInt(e.target.value)})} className="w-full p-2 border rounded-lg text-sm">
                        <option value="1">Half</option>
                        <option value="2">Full</option>
                    </select>
                </div>
            </div>
            {f.field_type === 'select' && (
                <div>
                    <label className="text-xs text-gray-500 uppercase font-bold block mb-1">Options (pipe-separated)</label>
                    <input value={f.options} onChange={e => setF({...f, options: e.target.value})} placeholder="Option 1|Option 2|Option 3" className="w-full p-2 border rounded-lg text-sm font-mono" />
                </div>
            )}
            <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={!!f.is_required} onChange={e => setF({...f, is_required: e.target.checked ? 1 : 0})} className="w-4 h-4 accent-[#EF5D22]" />
                    Required
                </label>
                <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 font-bold">Sort Order</label>
                    <input type="number" value={f.sort_order} onChange={e => setF({...f, sort_order: parseInt(e.target.value)})} className="w-16 p-1.5 border rounded text-center text-sm" />
                </div>
                <div className="ml-auto flex gap-2">
                    <button onClick={() => { if (!f.label.trim()) return; onAdd(f); setOpen(false); setF({ field_type: 'text', label: '', placeholder: '', options: '', is_required: 0, col_span: 1, sort_order: 99 }); }} className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1"><CheckIcon className="w-4 h-4" /> Save</button>
                    <button onClick={() => setOpen(false)} className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1"><XMarkIcon className="w-4 h-4" /> Cancel</button>
                </div>
            </div>
        </div>
    );
};

// ── Projects Admin Tab ────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
    { value: 'active',    label: 'Available' },
    { value: 'upcoming',  label: 'Upcoming'  },
    { value: 'completed', label: 'Sold Out'  },
];

const EMPTY_PROJECT = {
    name: '', location: '', tagline: '', description: '',
    image_url: '', brochure_url: '', sort_order: 0,
    status: 'active', video_url: '', highlights: '',
    price_range: '', plot_sizes: '', area: '',
};

interface ProjectsAdminTabProps {
    projects: any[];
    handleAction: (action: string, data: any) => void;
    refreshContent: () => void;
}

const ProjectsAdminTab: React.FC<ProjectsAdminTabProps> = ({ projects, handleAction, refreshContent }) => {
    const [form, setForm] = useState<any>(EMPTY_PROJECT);
    const [editId, setEditId] = useState<number | null>(null);
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [uploadingBrochure, setUploadingBrochure] = useState(false);
    const [uploadingMedia, setUploadingMedia] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
    const [mediaCaption, setMediaCaption] = useState('');
    const [mediaError, setMediaError] = useState('');
    const [saving, setSaving] = useState(false);

    const isEditing = editId !== null;

    const startAdd = () => {
        setForm({ ...EMPTY_PROJECT });
        setEditId(-1); // -1 = new
        setExpandedId(null);
    };

    const startEdit = (p: any) => {
        setForm({
            name: p.name || '', location: p.location || '',
            tagline: p.tagline || '', description: p.description || '',
            image_url: p.image_url || '', brochure_url: p.brochure_url || '',
            sort_order: p.sort_order || 0, status: p.status || 'active',
            video_url: p.video_url || '', highlights: p.highlights || '',
            price_range: p.price_range || '', plot_sizes: p.plot_sizes || '',
            area: p.area || '',
        });
        setEditId(p.id);
        setExpandedId(p.id);
    };

    const cancelEdit = () => { setForm({ ...EMPTY_PROJECT }); setEditId(null); };

    const saveProject = async () => {
        if (!form.name.trim()) { alert('Project name is required'); return; }
        setSaving(true);
        if (editId === -1) {
            await handleAction('add_project', form);
        } else {
            await handleAction('edit_project', { id: editId, ...form });
        }
        setSaving(false);
        setEditId(null);
        setForm({ ...EMPTY_PROJECT });
    };

    const uploadBrochure = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.type !== 'application/pdf') { alert('Only PDF files allowed for brochure'); return; }
        setUploadingBrochure(true);
        const fd = new FormData();
        fd.append('file', file);
        fd.append('folder', 'projects/brochures');
        try {
            const res = await fetch(UPLOAD_MEDIA_URL, {
                method: 'POST', body: fd, credentials: 'include',
            });
            const data = await res.json();
            if (data.status === 'success') {
                setForm((f: any) => ({ ...f, brochure_url: data.url }));
            } else { alert('Brochure upload failed: ' + data.message); }
        } catch { alert('Upload error'); }
        setUploadingBrochure(false);
        e.target.value = '';
    };

    const uploadMedia = async (e: React.ChangeEvent<HTMLInputElement>, projectId: number) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        setMediaError('');
        setUploadingMedia(true);
        setUploadProgress({ done: 0, total: files.length });

        let successCount = 0;
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const fd = new FormData();
            fd.append('file', file);
            const isVideo = file.type.startsWith('video/');
            fd.append('folder', isVideo ? 'projects/videos' : 'projects');
            try {
                const res = await fetch(UPLOAD_MEDIA_URL, {
                    method: 'POST', body: fd, credentials: 'include',
                });
                const data = await res.json();
                if (data.status === 'success') {
                    await handleAction('add_project_media', {
                        project_id: projectId,
                        media_type: isVideo ? 'video' : 'image',
                        url: data.url,
                        caption: files.length === 1 ? mediaCaption : '',
                        sort_order: 0,
                    });
                    successCount++;
                } else {
                    setMediaError(`Failed to upload ${file.name}: ${data.message}`);
                }
            } catch {
                setMediaError(`Error uploading ${file.name}`);
            }
            setUploadProgress({ done: i + 1, total: files.length });
        }

        if (successCount > 0) {
            setMediaCaption('');
        }
        setUploadingMedia(false);
        setUploadProgress(null);
        e.target.value = '';
    };

    const deleteMedia = async (mediaId: number) => {
        if (!window.confirm('Delete this media item?')) return;
        await handleAction('delete_project_media', { id: mediaId });
    };

    const setField = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

    return (
        <div className="space-y-6">
            {/* Add new button */}
            {editId === null && (
                <button
                    onClick={startAdd}
                    className="flex items-center gap-2 bg-[#EF5D22] text-white px-5 py-2.5 rounded-xl font-bold shadow hover:bg-[#d9541a] transition-all"
                >
                    <PlusIcon className="w-5 h-5" /> Add New Project
                </button>
            )}

            {/* Add / Edit form */}
            {editId !== null && (
                <div className="bg-gray-50 border-2 border-[#EF5D22]/20 rounded-2xl p-6 space-y-6">
                    <h3 className="font-bold text-lg text-gray-900">
                        {editId === -1 ? '➕ Add New Project' : '✏️ Edit Project'}
                    </h3>

                    {/* Row 1: Name + Status */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <input
                            placeholder="Project Name *"
                            value={form.name}
                            onChange={e => setField('name', e.target.value)}
                            className="p-2.5 border rounded-lg col-span-2 focus:ring-2 focus:ring-[#EF5D22]/30 focus:border-[#EF5D22] outline-none"
                        />
                        <select
                            value={form.status}
                            onChange={e => setField('status', e.target.value)}
                            className="p-2.5 border rounded-lg focus:ring-2 focus:ring-[#EF5D22]/30 focus:border-[#EF5D22] outline-none"
                        >
                            {STATUS_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Row 2: Location + Tagline */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input
                            placeholder="Location (e.g. Hyderabad, Telangana)"
                            value={form.location}
                            onChange={e => setField('location', e.target.value)}
                            className="p-2.5 border rounded-lg focus:ring-2 focus:ring-[#EF5D22]/30 focus:border-[#EF5D22] outline-none"
                        />
                        <input
                            placeholder="Tagline / Catchy line"
                            value={form.tagline}
                            onChange={e => setField('tagline', e.target.value)}
                            className="p-2.5 border rounded-lg focus:ring-2 focus:ring-[#EF5D22]/30 focus:border-[#EF5D22] outline-none"
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Description</label>
                        <textarea
                            placeholder="Short project description..."
                            value={form.description}
                            onChange={e => setField('description', e.target.value)}
                            rows={3}
                            className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-[#EF5D22]/30 focus:border-[#EF5D22] outline-none resize-none"
                        />
                    </div>

                    {/* Highlights */}
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">
                            Key Highlights
                            <span className="font-normal normal-case ml-1 text-gray-400">(separate with | pipe)</span>
                        </label>
                        <input
                            placeholder="e.g. DTCP Approved|TG RERA|Gated Community|24/7 Security"
                            value={form.highlights}
                            onChange={e => setField('highlights', e.target.value)}
                            className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-[#EF5D22]/30 focus:border-[#EF5D22] outline-none"
                        />
                        {form.highlights && (
                            <div className="flex flex-wrap gap-2 mt-2">
                                {form.highlights.split('|').filter(Boolean).map((h: string, i: number) => (
                                    <span key={i} className="bg-orange-100 text-orange-700 text-xs font-medium px-2.5 py-1 rounded-full">
                                        {h.trim()}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Specs Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Price Range</label>
                            <input
                                placeholder="e.g. ₹45L – ₹1.2Cr"
                                value={form.price_range}
                                onChange={e => setField('price_range', e.target.value)}
                                className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-[#EF5D22]/30 focus:border-[#EF5D22] outline-none"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Plot Sizes</label>
                            <input
                                placeholder="e.g. 150–400 sq.yd"
                                value={form.plot_sizes}
                                onChange={e => setField('plot_sizes', e.target.value)}
                                className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-[#EF5D22]/30 focus:border-[#EF5D22] outline-none"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Total Area</label>
                            <input
                                placeholder="e.g. 12 Acres"
                                value={form.area}
                                onChange={e => setField('area', e.target.value)}
                                className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-[#EF5D22]/30 focus:border-[#EF5D22] outline-none"
                            />
                        </div>
                    </div>

                    {/* Cover image */}
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Cover Image</label>
                        <ImageUploader
                            label="Upload Cover Image"
                            folder="projects"
                            value={form.image_url}
                            onChange={(url) => setField('image_url', url)}
                        />
                    </div>

                    {/* Brochure */}
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Brochure (PDF)</label>
                        <div className="flex items-center gap-3">
                            <label className={`cursor-pointer flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed text-sm font-bold transition-all ${uploadingBrochure ? 'border-gray-300 text-gray-400' : 'border-[#EF5D22]/40 text-[#EF5D22] hover:bg-orange-50'}`}>
                                {uploadingBrochure ? '⏳ Uploading...' : '📄 Upload PDF Brochure'}
                                <input type="file" accept=".pdf" className="hidden" onChange={uploadBrochure} disabled={uploadingBrochure} />
                            </label>
                            {form.brochure_url && (
                                <div className="flex items-center gap-2">
                                    <a href={form.brochure_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline truncate max-w-xs">
                                        {form.brochure_url.split('/').pop()}
                                    </a>
                                    <button title="Remove brochure" onClick={() => setField('brochure_url', '')} className="text-red-400 hover:text-red-600 text-lg leading-none">×</button>
                                </div>
                            )}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Or enter a URL manually:</p>
                        <input
                            placeholder="e.g. /brochures/elite-county.pdf or full URL"
                            value={form.brochure_url}
                            onChange={e => setField('brochure_url', e.target.value)}
                            className="mt-1 w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#EF5D22]/30 focus:border-[#EF5D22] outline-none"
                        />
                    </div>

                    {/* Video URL */}
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Video URL</label>
                        <input
                            placeholder="YouTube, Vimeo or direct video URL"
                            value={form.video_url}
                            onChange={e => setField('video_url', e.target.value)}
                            className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-[#EF5D22]/30 focus:border-[#EF5D22] outline-none"
                        />
                        <p className="text-xs text-gray-400 mt-1">e.g. https://www.youtube.com/watch?v=...</p>
                    </div>

                    {/* Sort order */}
                    <div className="w-32">
                        <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Sort Order</label>
                        <input
                            type="number"
                            value={form.sort_order}
                            onChange={e => setField('sort_order', e.target.value)}
                            className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-[#EF5D22]/30 focus:border-[#EF5D22] outline-none"
                        />
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-3 pt-2">
                        <button
                            onClick={saveProject}
                            disabled={saving}
                            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-xl font-bold transition-all disabled:opacity-60"
                        >
                            <CheckIcon className="w-4 h-4" />
                            {saving ? 'Saving…' : 'Save Project'}
                        </button>
                        <button
                            onClick={cancelEdit}
                            className="flex items-center gap-2 bg-gray-300 hover:bg-gray-400 text-gray-700 px-5 py-2.5 rounded-xl font-bold transition-all"
                        >
                            <XMarkIcon className="w-4 h-4" /> Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Project Cards List */}
            <div className="space-y-4">
                {projects.length === 0 && editId === null && (
                    <div className="text-center py-16 text-gray-400 border-2 border-dashed rounded-2xl">
                        <p className="text-lg font-medium">No projects yet</p>
                        <p className="text-sm mt-1">Click "Add New Project" to get started</p>
                    </div>
                )}
                {projects.map((project: any) => {
                    const isExpanded = expandedId === project.id;
                    const statusBadge: Record<string, string> = {
                        active: 'bg-green-100 text-green-700',
                        upcoming: 'bg-blue-100 text-blue-700',
                        completed: 'bg-gray-100 text-gray-600',
                    };
                    const statusLabel: Record<string, string> = {
                        active: 'Available', upcoming: 'Upcoming', completed: 'Sold Out',
                    };
                    return (
                        <div key={project.id} className="border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm">
                            {/* Card header */}
                            <div className="flex items-center gap-4 p-4">
                                <div
                                    className="w-20 h-16 rounded-xl overflow-hidden flex-shrink-0 cursor-pointer bg-gray-100"
                                    onClick={() => setExpandedId(isExpanded ? null : project.id)}
                                >
                                    {(project.image_url || project.image) ? (
                                        <img src={project.image_url || project.image} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-300 text-2xl">🏗</div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : project.id)}>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h4 className="font-bold text-gray-900 truncate">{project.name}</h4>
                                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusBadge[project.status] || statusBadge.active}`}>
                                            {statusLabel[project.status] || 'Available'}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-400 truncate">{project.location}</p>
                                    {project.price_range && <p className="text-xs font-bold text-[#EF5D22]">{project.price_range}</p>}
                                    <p className="text-xs text-gray-400 mt-0.5">
                                        {(project.media?.length || 0)} media item{(project.media?.length || 0) !== 1 ? 's' : ''}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <button
                                        title="Edit project"
                                        onClick={() => startEdit(project)}
                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                    >
                                        <PencilSquareIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                        title="Delete project"
                                        onClick={() => { if (window.confirm(`Delete "${project.name}"?`)) handleAction('delete_project', { id: project.id }); }}
                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                    >
                                        <TrashIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                        title={isExpanded ? 'Collapse' : 'Expand media'}
                                        onClick={() => setExpandedId(isExpanded ? null : project.id)}
                                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-all"
                                    >
                                        {isExpanded ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            {/* Expanded media manager */}
                            {isExpanded && (
                                <div className="border-t border-gray-100 bg-gray-50 p-5 space-y-5">
                                    <h5 className="font-bold text-gray-700 text-sm uppercase tracking-wider">
                                        📂 Media Gallery — {project.name}
                                    </h5>

                                    {/* Existing media grid */}
                                    {project.media && project.media.length > 0 ? (
                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                            {project.media.map((m: any) => (
                                                <div key={m.id} className="relative group rounded-xl overflow-hidden bg-white border border-gray-200 shadow-sm">
                                                    {m.media_type === 'video' ? (
                                                        <div className="h-24 bg-gray-900 flex items-center justify-center text-white text-2xl">
                                                            🎬
                                                        </div>
                                                    ) : (
                                                        <img src={m.url} alt={m.caption || ''} className="w-full h-24 object-cover" />
                                                    )}
                                                    <div className="p-1.5">
                                                        <p className="text-xs text-gray-500 truncate">{m.caption || (m.media_type === 'video' ? 'Video' : 'Image')}</p>
                                                        <p className="text-[10px] text-gray-300 truncate">{m.url.split('/').pop()}</p>
                                                    </div>
                                                    <button
                                                        title="Delete media"
                                                        onClick={() => deleteMedia(m.id)}
                                                        className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs leading-none"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-400 italic">No media uploaded yet for this project.</p>
                                    )}

                                    {/* Upload new media */}
                                    <div className="bg-white border border-dashed border-gray-300 rounded-xl p-4 space-y-3">
                                        <p className="text-sm font-bold text-gray-600">Upload New Media</p>
                                        <input
                                            placeholder="Caption (optional)"
                                            value={mediaCaption}
                                            onChange={e => setMediaCaption(e.target.value)}
                                            className="w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#EF5D22]/30 focus:border-[#EF5D22] outline-none"
                                        />
                                        <p className="text-xs text-gray-400">💡 Tip: You can select multiple images at once (Ctrl/Cmd + Click)</p>
                                        <div className="flex flex-wrap gap-3">
                                            <label className={`cursor-pointer flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-bold transition-all ${uploadingMedia ? 'opacity-50 pointer-events-none' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}>
                                                🖼 Add Images (Bulk)
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    multiple
                                                    className="hidden"
                                                    onChange={e => uploadMedia(e, project.id)}
                                                    disabled={uploadingMedia}
                                                />
                                            </label>
                                            <label className={`cursor-pointer flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-bold transition-all ${uploadingMedia ? 'opacity-50 pointer-events-none' : 'border-purple-300 text-purple-600 hover:bg-purple-50'}`}>
                                                🎬 Add Videos (Bulk)
                                                <input
                                                    type="file"
                                                    accept="video/*"
                                                    multiple
                                                    className="hidden"
                                                    onChange={e => uploadMedia(e, project.id)}
                                                    disabled={uploadingMedia}
                                                />
                                            </label>
                                        </div>
                                        {uploadingMedia && uploadProgress && (
                                            <div className="space-y-2">
                                                <p className="text-sm text-orange-500 font-medium">
                                                    ⏳ Uploading {uploadProgress.done} of {uploadProgress.total}...
                                                </p>
                                                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-[#EF5D22] transition-all duration-300"
                                                        style={{ width: `${(uploadProgress.done / uploadProgress.total) * 100}%` }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        {mediaError && <p className="text-sm text-red-500">{mediaError}</p>}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── Main Admin Component ─────────────────────────────────────────────────────

const Admin = () => {
    const { content, refreshContent } = useContent();
    const { username, logout } = useAuth();
    const [activeTab, setActiveTab] = useState('contact');
    const [status, setStatus] = useState({ type: '', message: '' });
    const [editingItem, setEditingItem] = useState<any>(null);
    const [isAdding, setIsAdding] = useState(false);

    // Form states
    const [contactForm, setContactForm] = useState(content.common);
    const [generalForm, setGeneralForm] = useState<any>({});
    const [newItemForm, setNewItemForm] = useState<any>({});

    useEffect(() => {
        setContactForm(content.common);
        // Initialize general form with available content
        const gen: any = {};
        ['about', 'chairman', 'projects', 'highlights', 'amenities', 'hero', 'faq', 'branding'].forEach(section => {
            const sectionData = (content as Record<string, any>)[section];
            if (sectionData) {
                Object.keys(sectionData).forEach(key => {
                    if (typeof sectionData[key] === 'string') {
                        gen[`${section}_${key}`] = sectionData[key];
                    }
                });
            }
        });
        setGeneralForm(gen);
    }, [content]);

    const showStatus = (type: string, message: string) => {
        setStatus({ type, message });
        setTimeout(() => setStatus({ type: '', message: '' }), 3000);
    };

    const handleAction = async (action: string, data: any) => {
        try {
            const response = await fetch(UPDATE_CONTENT_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',   // send httpOnly admin_token cookie
                body: JSON.stringify({ action, ...data })
            });
            const result = await response.json();
            if (result.status === 'success') {
                showStatus('success', result.message);
                setEditingItem(null);
                setIsAdding(false);
                setNewItemForm({});
                refreshContent();
            } else {
                showStatus('error', result.message);
            }
        } catch (error) {
            showStatus('error', 'Operation failed');
        }
    };

    const handleGeneralUpdate = (section: string, key: string, value: string) => {
        handleAction('update_general', { section_key: section, item_key: key, value });
    };

    const tabs = [
        { id: 'branding',   label: '🎨 Branding' },
        { id: 'contact',    label: 'Contact & Social' },
        { id: 'menus',      label: 'Menus' },
        { id: 'site_pages', label: 'Site Pages' },
        { id: 'pages',      label: 'Custom Pages' },
        { id: 'sliders',    label: 'Hero Sliders' },
        { id: 'projects',   label: 'Projects' },
        { id: 'highlights', label: 'Highlights' },
        { id: 'amenities',  label: 'Amenities' },
        { id: 'faq',          label: 'FAQ' },
        { id: 'footer',       label: '🦶 Footer' },
        { id: 'contact_page', label: '📋 Contact Page' },
    ];

    return (
        <div className="min-h-screen bg-gray-50 flex">
            {/* Sidebar */}
            <div className="w-64 bg-[#0A1D2D] text-white min-h-screen sticky top-0 flex flex-col">
                <div className="p-6 border-b border-gray-700">
                    <h1 className="text-xl font-bold text-[#EF5D22]">Abhayasri Admin</h1>
                    <p className="text-xs text-gray-400 mt-1">Management Portal v1.0</p>
                </div>
                <nav className="mt-6 flex-1">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => { setActiveTab(tab.id); setEditingItem(null); setIsAdding(false); }}
                            className={`w-full text-left px-6 py-3 text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-[#EF5D22] text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
                {/* Logged-in user + Logout */}
                <div className="p-4 border-t border-gray-700">
                    <p className="text-xs text-gray-400 mb-2 truncate">
                        Signed in as <span className="text-white font-semibold">{username}</span>
                    </p>
                    <button
                        onClick={async () => { if (window.confirm('Sign out?')) await logout(); }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-400 hover:bg-red-900/30 hover:text-red-300 rounded-lg transition-colors"
                    >
                        <ArrowRightOnRectangleIcon className="w-4 h-4" />
                        Sign Out
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 p-10">
                <div className="max-w-6xl mx-auto">
                    {status.message && (
                        <div className={`fixed top-5 right-5 z-50 p-4 rounded-lg shadow-xl animate-bounce ${status.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                            {status.message}
                        </div>
                    )}

                    <div className="flex justify-between items-center mb-8">
                        <h2 className="text-3xl font-bold text-gray-900">{tabs.find(t => t.id === activeTab)?.label}</h2>
                        {['menus', 'sliders', 'projects', 'highlights', 'amenities', 'faq', 'pages'].includes(activeTab) && !isAdding && (                            <button 
                                onClick={() => setIsAdding(true)}
                                className="flex items-center space-x-2 bg-[#EF5D22] text-white px-4 py-2 rounded-lg font-bold hover:bg-[#d9541a] transition-all"
                            >
                                <PlusIcon className="w-5 h-5" />
                                <span>Add New</span>
                            </button>
                        )}
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="p-8">
                            {/* CONTACT TAB */}
                            {activeTab === 'contact' && (
                                <form onSubmit={(e) => { e.preventDefault(); handleAction('update_contact', { ...contactForm, ...contactForm.social }); }} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="space-y-4">
                                            <h3 className="text-lg font-semibold border-b pb-2">Basic Information</h3>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                                                <input type="text" value={contactForm.phone} onChange={(e) => setContactForm({...contactForm, phone: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-[#EF5D22] focus:border-[#EF5D22] p-2.5 border" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Email Address</label>
                                                <input type="email" value={contactForm.email} onChange={(e) => setContactForm({...contactForm, email: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-[#EF5D22] focus:border-[#EF5D22] p-2.5 border" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Short Address</label>
                                                <input type="text" value={contactForm.address} onChange={(e) => setContactForm({...contactForm, address: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-[#EF5D22] focus:border-[#EF5D22] p-2.5 border" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Full Address (Footer)</label>
                                                <textarea value={contactForm.fullAddress} onChange={(e) => setContactForm({...contactForm, fullAddress: e.target.value})} className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-[#EF5D22] focus:border-[#EF5D22] p-2.5 border" rows={3} />
                                            </div>
                                        </div>
                                        <div className="space-y-4">
                                            <h3 className="text-lg font-semibold border-b pb-2">Social Media Links</h3>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Facebook URL</label>
                                                <input type="text" value={contactForm.social.facebook} onChange={(e) => setContactForm({...contactForm, social: {...contactForm.social, facebook: e.target.value}})} className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-[#EF5D22] focus:border-[#EF5D22] p-2.5 border" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Instagram URL</label>
                                                <input type="text" value={contactForm.social.instagram} onChange={(e) => setContactForm({...contactForm, social: {...contactForm.social, instagram: e.target.value}})} className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-[#EF5D22] focus:border-[#EF5D22] p-2.5 border" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">WhatsApp URL / Number</label>
                                                <input type="text" value={contactForm.social.whatsapp} onChange={(e) => setContactForm({...contactForm, social: {...contactForm.social, whatsapp: e.target.value}})} className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-[#EF5D22] focus:border-[#EF5D22] p-2.5 border" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Telegram URL</label>
                                                <input type="text" value={contactForm.social.telegram} onChange={(e) => setContactForm({...contactForm, social: {...contactForm.social, telegram: e.target.value}})} className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-[#EF5D22] focus:border-[#EF5D22] p-2.5 border" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex justify-end pt-4">
                                        <button type="submit" className="bg-[#EF5D22] text-white px-8 py-3 rounded-xl font-bold shadow-lg hover:bg-[#d9541a] transition-all transform hover:scale-105">
                                            Update Contact Information
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* MENUS TAB */}
                            {activeTab === 'menus' && (
                                <div className="space-y-6">
                                    {(isAdding || editingItem) && (
                                        <div className="bg-gray-50 p-6 rounded-xl border-2 border-[#EF5D22]/20 mb-8">
                                            <h3 className="font-bold text-lg mb-4">{isAdding ? 'Add New Menu Item' : 'Edit Menu Item'}</h3>
                                            <div className="grid grid-cols-2 gap-4">
                                                <input placeholder="Title (e.g. Home)" value={isAdding ? newItemForm.title : editingItem.title} onChange={(e) => isAdding ? setNewItemForm({...newItemForm, title: e.target.value}) : setEditingItem({...editingItem, title: e.target.value})} className="p-2 border rounded" />
                                                <input placeholder="Link (e.g. /home)" value={isAdding ? newItemForm.link : editingItem.link} onChange={(e) => isAdding ? setNewItemForm({...newItemForm, link: e.target.value}) : setEditingItem({...editingItem, link: e.target.value})} className="p-2 border rounded" />
                                                <div className="col-span-2">
                                                    <IconPicker
                                                        label="Icon"
                                                        value={isAdding ? (newItemForm.icon || '') : (editingItem.icon || '')}
                                                        onChange={(name) => isAdding ? setNewItemForm({...newItemForm, icon: name}) : setEditingItem({...editingItem, icon: name})}
                                                    />
                                                </div>
                                                <input type="number" placeholder="Sort Order" value={isAdding ? newItemForm.sort_order : editingItem.sort_order} onChange={(e) => isAdding ? setNewItemForm({...newItemForm, sort_order: e.target.value}) : setEditingItem({...editingItem, sort_order: e.target.value})} className="p-2 border rounded" />
                                            </div>
                                            <div className="mt-4 flex space-x-2">
                                                <button onClick={() => handleAction(isAdding ? 'add_menu' : 'edit_menu', isAdding ? newItemForm : editingItem)} className="bg-green-600 text-white px-4 py-2 rounded flex items-center"><CheckIcon className="w-4 h-4 mr-1" /> Save</button>
                                                <button onClick={() => { setIsAdding(false); setEditingItem(null); }} className="bg-gray-400 text-white px-4 py-2 rounded flex items-center"><XMarkIcon className="w-4 h-4 mr-1" /> Cancel</button>
                                            </div>
                                        </div>
                                    )}
                                    <div className="grid gap-4">
                                        {((content as any).navArray as any[] || []).map((item: any) => (
                                            <div key={item.id} className="flex justify-between items-center p-4 border rounded-xl hover:bg-gray-50 transition-colors">
                                                <div className="flex items-center space-x-4">
                                                    <span className="bg-gray-100 p-2 rounded text-xs font-mono">{item.sort_order}</span>
                                                    <div>
                                                        <p className="font-bold text-gray-900">{item.title}</p>
                                                        <p className="text-xs text-gray-500">{item.link}</p>
                                                    </div>
                                                </div>
                                                <div className="flex space-x-2">
                                                    <button onClick={() => setEditingItem(item)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><PencilSquareIcon className="w-5 h-5" /></button>
                                                    <button onClick={() => { if(window.confirm('Delete this menu item?')) handleAction('delete_menu', { id: item.id }); }} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><TrashIcon className="w-5 h-5" /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* PROJECTS TAB */}
                            {activeTab === 'projects' && (
                                <ProjectsAdminTab
                                    projects={content.projects.items}
                                    handleAction={handleAction}
                                    refreshContent={refreshContent}
                                />
                            )}

                            {/* HIGHLIGHTS TAB */}
                            {activeTab === 'highlights' && (
                                <div className="space-y-6">
                                    {(isAdding || editingItem) && (
                                        <div className="bg-gray-50 p-6 rounded-xl border-2 border-[#EF5D22]/20 mb-8">
                                            <h3 className="font-bold text-lg mb-4">{isAdding ? 'Add New Highlight' : 'Edit Highlight'}</h3>
                                            <div className="grid gap-4">
                                                <input placeholder="Title (e.g. 5 Mins to NH-65)" value={isAdding ? newItemForm.title : editingItem.title} onChange={(e) => isAdding ? setNewItemForm({...newItemForm, title: e.target.value}) : setEditingItem({...editingItem, title: e.target.value})} className="p-2 border rounded" />
                                                <textarea placeholder="Description" value={isAdding ? newItemForm.description : editingItem.description} onChange={(e) => isAdding ? setNewItemForm({...newItemForm, description: e.target.value}) : setEditingItem({...editingItem, description: e.target.value})} className="p-2 border rounded" rows={2} />
                                                <IconPicker
                                                    label="Icon"
                                                    value={isAdding ? (newItemForm.icon || '') : (editingItem.icon || '')}
                                                    onChange={(name) => isAdding ? setNewItemForm({...newItemForm, icon: name}) : setEditingItem({...editingItem, icon: name})}
                                                />
                                                <input type="number" placeholder="Sort Order" value={isAdding ? newItemForm.sort_order : editingItem.sort_order} onChange={(e) => isAdding ? setNewItemForm({...newItemForm, sort_order: e.target.value}) : setEditingItem({...editingItem, sort_order: e.target.value})} className="p-2 border rounded" />
                                            </div>
                                            <div className="mt-4 flex space-x-2">
                                                <button onClick={() => handleAction(isAdding ? 'add_highlight' : 'edit_highlight', isAdding ? newItemForm : editingItem)} className="bg-green-600 text-white px-4 py-2 rounded flex items-center"><CheckIcon className="w-4 h-4 mr-1" /> Save</button>
                                                <button onClick={() => { setIsAdding(false); setEditingItem(null); }} className="bg-gray-400 text-white px-4 py-2 rounded flex items-center"><XMarkIcon className="w-4 h-4 mr-1" /> Cancel</button>
                                            </div>
                                        </div>
                                    )}
                                    <div className="grid gap-4">
                                        {content.highlights.items.map((item: any) => (
                                            <div key={item.id} className="flex justify-between items-center p-4 border rounded-xl">
                                                <div>
                                                    <p className="font-bold">{item.title}</p>
                                                    <p className="text-sm text-gray-500">{item.description}</p>
                                                </div>
                                                <div className="flex space-x-2">
                                                    <button onClick={() => setEditingItem(item)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><PencilSquareIcon className="w-5 h-5" /></button>
                                                    <button onClick={() => { if(window.confirm('Delete this highlight?')) handleAction('delete_highlight', { id: item.id }); }} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><TrashIcon className="w-5 h-5" /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* AMENITIES TAB */}
                            {activeTab === 'amenities' && (
                                <div className="space-y-6">
                                    {(isAdding || editingItem) && (
                                        <div className="bg-gray-50 p-6 rounded-xl border-2 border-[#EF5D22]/20 mb-8">
                                            <h3 className="font-bold text-lg mb-4">{isAdding ? 'Add New Amenity' : 'Edit Amenity'}</h3>
                                            <div className="grid gap-4">
                                                <input placeholder="Title" value={isAdding ? newItemForm.title : editingItem.title} onChange={(e) => isAdding ? setNewItemForm({...newItemForm, title: e.target.value}) : setEditingItem({...editingItem, title: e.target.value})} className="p-2 border rounded" />
                                                <textarea placeholder="Hover Text / Description" value={isAdding ? newItemForm.hover_text : editingItem.hover_text} onChange={(e) => isAdding ? setNewItemForm({...newItemForm, hover_text: e.target.value}) : setEditingItem({...editingItem, hover_text: e.target.value})} className="p-2 border rounded" rows={2} />
                                                <IconPicker
                                                    label="Icon"
                                                    value={isAdding ? (newItemForm.icon || '') : (editingItem.icon || '')}
                                                    onChange={(name) => isAdding ? setNewItemForm({...newItemForm, icon: name}) : setEditingItem({...editingItem, icon: name})}
                                                />
                                                <input type="number" placeholder="Sort Order" value={isAdding ? newItemForm.sort_order : editingItem.sort_order} onChange={(e) => isAdding ? setNewItemForm({...newItemForm, sort_order: e.target.value}) : setEditingItem({...editingItem, sort_order: e.target.value})} className="p-2 border rounded" />
                                            </div>
                                            <div className="mt-4 flex space-x-2">
                                                <button onClick={() => handleAction(isAdding ? 'add_amenity' : 'edit_amenity', isAdding ? newItemForm : editingItem)} className="bg-green-600 text-white px-4 py-2 rounded flex items-center"><CheckIcon className="w-4 h-4 mr-1" /> Save</button>
                                                <button onClick={() => { setIsAdding(false); setEditingItem(null); }} className="bg-gray-400 text-white px-4 py-2 rounded flex items-center"><XMarkIcon className="w-4 h-4 mr-1" /> Cancel</button>
                                            </div>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {content.amenities.items.map((item: any) => (
                                            <div key={item.id} className="flex justify-between items-center p-4 border rounded-xl">
                                                <div>
                                                    <p className="font-bold">{item.title}</p>
                                                    <p className="text-xs text-gray-500 truncate w-40">{item.hover_text}</p>
                                                </div>
                                                <div className="flex space-x-2">
                                                    <button onClick={() => setEditingItem(item)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><PencilSquareIcon className="w-4 h-4" /></button>
                                                    <button onClick={() => { if(window.confirm('Delete this amenity?')) handleAction('delete_amenity', { id: item.id }); }} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><TrashIcon className="w-4 h-4" /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* BRANDING TAB */}
                            {activeTab === 'branding' && (
                                <div className="space-y-10">
                                    {/* Logo */}
                                    <div className="space-y-4">
                                        <h3 className="text-xl font-bold border-b pb-2 flex items-center gap-2">
                                            <span>🖼️</span> Site Logo
                                        </h3>
                                        <p className="text-sm text-gray-500">The logo shown in the header navigation. Upload a PNG/SVG with a transparent background for best results.</p>
                                        <div className="flex flex-col sm:flex-row gap-8 items-start">
                                            {/* Current logo preview */}
                                            <div className="flex-shrink-0">
                                                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Current Logo</p>
                                                <div className="w-48 h-20 bg-[#0A1D2D] rounded-xl flex items-center justify-center overflow-hidden px-4">
                                                    <img
                                                        src={(content as any).logo_url || '/images/mainLogo.png'}
                                                        alt="Current Logo"
                                                        className="max-h-14 max-w-full object-contain"
                                                    />
                                                </div>
                                            </div>
                                            {/* Uploader */}
                                            <div className="flex-1">
                                                <ImageUploader
                                                    label="Upload New Logo"
                                                    folder="general"
                                                    value={(content as any).logo_url || ''}
                                                    onChange={(url) => handleAction('update_logo', { logo_url: url })}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Company Name */}
                                    <div className="space-y-4">
                                        <h3 className="text-xl font-bold border-b pb-2 flex items-center gap-2">
                                            <span>🏢</span> Company Name
                                        </h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Company Name (Bold)</label>
                                                <input
                                                    value={generalForm.branding_companyName || ''}
                                                    onChange={e => setGeneralForm({ ...generalForm, branding_companyName: e.target.value })}
                                                    onBlur={() => handleGeneralUpdate('branding', 'companyName', generalForm.branding_companyName)}
                                                    placeholder="ABHAYASRI"
                                                    className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-[#EF5D22] outline-none"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Sub Name</label>
                                                <input
                                                    value={generalForm.branding_companySubName || ''}
                                                    onChange={e => setGeneralForm({ ...generalForm, branding_companySubName: e.target.value })}
                                                    onBlur={() => handleGeneralUpdate('branding', 'companySubName', generalForm.branding_companySubName)}
                                                    placeholder="Infra Developers"
                                                    className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-[#EF5D22] outline-none"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* SITE PAGES TAB — manage content of built-in pages */}
                            {activeTab === 'site_pages' && (
                                <div className="space-y-2">
                                    <p className="text-sm text-gray-500 mb-6">Edit the text content of the built-in website pages. Changes are saved automatically when you leave each field (on blur).</p>

                                    {/* ── HOME / HERO ── */}
                                    <details className="border rounded-xl overflow-hidden group" open>
                                        <summary className="flex items-center justify-between p-5 cursor-pointer bg-gray-50 hover:bg-gray-100 font-bold text-gray-800 select-none">
                                            <span>🏠 Home — Hero Section</span>
                                            <ChevronDownIcon className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
                                        </summary>
                                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white">
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title Prefix</label>
                                                <input value={generalForm.hero_titlePrefix || ''} onChange={e => setGeneralForm({...generalForm, hero_titlePrefix: e.target.value})} onBlur={() => handleGeneralUpdate('hero', 'titlePrefix', generalForm.hero_titlePrefix)} placeholder="Invest in Your" className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title Middle</label>
                                                <input value={generalForm.hero_titleMiddle || ''} onChange={e => setGeneralForm({...generalForm, hero_titleMiddle: e.target.value})} onBlur={() => handleGeneralUpdate('hero', 'titleMiddle', generalForm.hero_titleMiddle)} placeholder="Future: Premium" className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title Highlight (Orange)</label>
                                                <input value={generalForm.hero_titleHighlight || ''} onChange={e => setGeneralForm({...generalForm, hero_titleHighlight: e.target.value})} onBlur={() => handleGeneralUpdate('hero', 'titleHighlight', generalForm.hero_titleHighlight)} placeholder="Villa Plots" className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title Suffix</label>
                                                <input value={generalForm.hero_titleSuffix || ''} onChange={e => setGeneralForm({...generalForm, hero_titleSuffix: e.target.value})} onBlur={() => handleGeneralUpdate('hero', 'titleSuffix', generalForm.hero_titleSuffix)} placeholder="at Elite County" className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Description</label>
                                                <RichTextEditor
                                                    value={generalForm.hero_description || ''}
                                                    onChange={v => { setGeneralForm({...generalForm, hero_description: v}); handleGeneralUpdate('hero', 'description', v); }}
                                                    placeholder="Hero description text…"
                                                    minHeight={100}
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">CTA Button Text</label>
                                                <input value={generalForm.hero_ctaButton || ''} onChange={e => setGeneralForm({...generalForm, hero_ctaButton: e.target.value})} onBlur={() => handleGeneralUpdate('hero', 'ctaButton', generalForm.hero_ctaButton)} placeholder="Get Offer Details" className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Form Title</label>
                                                <input value={generalForm.hero_formTitle || ''} onChange={e => setGeneralForm({...generalForm, hero_formTitle: e.target.value})} onBlur={() => handleGeneralUpdate('hero', 'formTitle', generalForm.hero_formTitle)} placeholder="Get Exclusive Pricing & Brochure" className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                        </div>
                                    </details>

                                    {/* ── ABOUT ── */}
                                    <details className="border rounded-xl overflow-hidden group">
                                        <summary className="flex items-center justify-between p-5 cursor-pointer bg-gray-50 hover:bg-gray-100 font-bold text-gray-800 select-none">
                                            <span>ℹ️ About Page</span>
                                            <ChevronDownIcon className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
                                        </summary>
                                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white">
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Badge Text</label>
                                                <input value={generalForm.about_badge || ''} onChange={e => setGeneralForm({...generalForm, about_badge: e.target.value})} onBlur={() => handleGeneralUpdate('about', 'badge', generalForm.about_badge)} className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title Part 1</label>
                                                <input value={generalForm.about_titlePart1 || ''} onChange={e => setGeneralForm({...generalForm, about_titlePart1: e.target.value})} onBlur={() => handleGeneralUpdate('about', 'titlePart1', generalForm.about_titlePart1)} className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title Part 2 (Orange Highlight)</label>
                                                <input value={generalForm.about_titlePart2 || ''} onChange={e => setGeneralForm({...generalForm, about_titlePart2: e.target.value})} onBlur={() => handleGeneralUpdate('about', 'titlePart2', generalForm.about_titlePart2)} className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Paragraph 1</label>
                                                <RichTextEditor
                                                    value={generalForm.about_p1 || ''}
                                                    onChange={v => { setGeneralForm({...generalForm, about_p1: v}); handleGeneralUpdate('about', 'p1', v); }}
                                                    placeholder="About paragraph 1…"
                                                    minHeight={100}
                                                />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Paragraph 2</label>
                                                <RichTextEditor
                                                    value={generalForm.about_p2 || ''}
                                                    onChange={v => { setGeneralForm({...generalForm, about_p2: v}); handleGeneralUpdate('about', 'p2', v); }}
                                                    placeholder="About paragraph 2…"
                                                    minHeight={100}
                                                />
                                            </div>
                                        </div>
                                    </details>

                                    {/* ── CHAIRMAN ── */}
                                    <details className="border rounded-xl overflow-hidden group">
                                        <summary className="flex items-center justify-between p-5 cursor-pointer bg-gray-50 hover:bg-gray-100 font-bold text-gray-800 select-none">
                                            <span>👔 Chairman's Message</span>
                                            <ChevronDownIcon className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
                                        </summary>
                                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white">
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Chairman Name</label>
                                                <input value={generalForm.chairman_name || ''} onChange={e => setGeneralForm({...generalForm, chairman_name: e.target.value})} onBlur={() => handleGeneralUpdate('chairman', 'name', generalForm.chairman_name)} className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Designation</label>
                                                <input value={generalForm.chairman_designation || ''} onChange={e => setGeneralForm({...generalForm, chairman_designation: e.target.value})} onBlur={() => handleGeneralUpdate('chairman', 'designation', generalForm.chairman_designation)} className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Quote / Message</label>
                                                <RichTextEditor
                                                    value={generalForm.chairman_quote || ''}
                                                    onChange={v => { setGeneralForm({...generalForm, chairman_quote: v}); handleGeneralUpdate('chairman', 'quote', v); }}
                                                    placeholder="Chairman's message or quote…"
                                                    minHeight={100}
                                                />
                                            </div>
                                        </div>
                                    </details>

                                    {/* ── HIGHLIGHTS PAGE HEADER ── */}
                                    <details className="border rounded-xl overflow-hidden group">
                                        <summary className="flex items-center justify-between p-5 cursor-pointer bg-gray-50 hover:bg-gray-100 font-bold text-gray-800 select-none">
                                            <span>⭐ Highlights Page — Section Header</span>
                                            <ChevronDownIcon className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
                                        </summary>
                                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white">
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Badge Text</label>
                                                <input value={generalForm.highlights_badge || ''} onChange={e => setGeneralForm({...generalForm, highlights_badge: e.target.value})} onBlur={() => handleGeneralUpdate('highlights', 'badge', generalForm.highlights_badge)} placeholder="Location Advantage" className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title Part 1</label>
                                                <input value={generalForm.highlights_titlePart1 || ''} onChange={e => setGeneralForm({...generalForm, highlights_titlePart1: e.target.value})} onBlur={() => handleGeneralUpdate('highlights', 'titlePart1', generalForm.highlights_titlePart1)} placeholder="Project" className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title Part 2 (Orange)</label>
                                                <input value={generalForm.highlights_titlePart2 || ''} onChange={e => setGeneralForm({...generalForm, highlights_titlePart2: e.target.value})} onBlur={() => handleGeneralUpdate('highlights', 'titlePart2', generalForm.highlights_titlePart2)} placeholder="Highlights" className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Description</label>
                                                <input value={generalForm.highlights_description || ''} onChange={e => setGeneralForm({...generalForm, highlights_description: e.target.value})} onBlur={() => handleGeneralUpdate('highlights', 'description', generalForm.highlights_description)} className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                        </div>
                                    </details>

                                    {/* ── AMENITIES PAGE HEADER ── */}
                                    <details className="border rounded-xl overflow-hidden group">
                                        <summary className="flex items-center justify-between p-5 cursor-pointer bg-gray-50 hover:bg-gray-100 font-bold text-gray-800 select-none">
                                            <span>🏢 Amenities Page — Section Header</span>
                                            <ChevronDownIcon className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
                                        </summary>
                                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white">
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Badge Text</label>
                                                <input value={generalForm.amenities_badge || ''} onChange={e => setGeneralForm({...generalForm, amenities_badge: e.target.value})} onBlur={() => handleGeneralUpdate('amenities', 'badge', generalForm.amenities_badge)} placeholder="World Class Features" className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title Part 1</label>
                                                <input value={generalForm.amenities_titlePart1 || ''} onChange={e => setGeneralForm({...generalForm, amenities_titlePart1: e.target.value})} onBlur={() => handleGeneralUpdate('amenities', 'titlePart1', generalForm.amenities_titlePart1)} placeholder="Modern" className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title Part 2 (Orange)</label>
                                                <input value={generalForm.amenities_titlePart2 || ''} onChange={e => setGeneralForm({...generalForm, amenities_titlePart2: e.target.value})} onBlur={() => handleGeneralUpdate('amenities', 'titlePart2', generalForm.amenities_titlePart2)} placeholder="Amenities" className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Description</label>
                                                <input value={generalForm.amenities_description || ''} onChange={e => setGeneralForm({...generalForm, amenities_description: e.target.value})} onBlur={() => handleGeneralUpdate('amenities', 'description', generalForm.amenities_description)} className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                        </div>
                                    </details>

                                    {/* ── PROJECTS SECTION HEADER ── */}
                                    <details className="border rounded-xl overflow-hidden group">
                                        <summary className="flex items-center justify-between p-5 cursor-pointer bg-gray-50 hover:bg-gray-100 font-bold text-gray-800 select-none">
                                            <span>🏗️ Projects Page — Section Header</span>
                                            <ChevronDownIcon className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
                                        </summary>
                                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white">
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Badge Text</label>
                                                <input value={generalForm.projects_badge || ''} onChange={e => setGeneralForm({...generalForm, projects_badge: e.target.value})} onBlur={() => handleGeneralUpdate('projects', 'badge', generalForm.projects_badge)} placeholder="Our Projects" className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title Part 1</label>
                                                <input value={generalForm.projects_titlePart1 || ''} onChange={e => setGeneralForm({...generalForm, projects_titlePart1: e.target.value})} onBlur={() => handleGeneralUpdate('projects', 'titlePart1', generalForm.projects_titlePart1)} placeholder="Explore Our" className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title Part 2 (Orange)</label>
                                                <input value={generalForm.projects_titlePart2 || ''} onChange={e => setGeneralForm({...generalForm, projects_titlePart2: e.target.value})} onBlur={() => handleGeneralUpdate('projects', 'titlePart2', generalForm.projects_titlePart2)} placeholder="Landmark Developments" className="w-full p-2.5 border rounded-lg" />
                                            </div>
                                        </div>
                                    </details>
                                </div>
                            )}

                            {/* FAQ TAB */}
                            {activeTab === 'faq' && (
                                <div className="space-y-6">
                                    {(isAdding || editingItem) && (
                                        <div className="bg-gray-50 p-6 rounded-xl border-2 border-[#EF5D22]/20 mb-8">
                                            <h3 className="font-bold text-lg mb-4">{isAdding ? 'Add New FAQ' : 'Edit FAQ'}</h3>
                                            <div className="grid gap-4">
                                                <input placeholder="Question" value={isAdding ? newItemForm.question : editingItem.question} onChange={(e) => isAdding ? setNewItemForm({...newItemForm, question: e.target.value}) : setEditingItem({...editingItem, question: e.target.value})} className="p-2 border rounded" />
                                                <textarea placeholder="Answer" value={isAdding ? newItemForm.answer : editingItem.answer} onChange={(e) => isAdding ? setNewItemForm({...newItemForm, answer: e.target.value}) : setEditingItem({...editingItem, answer: e.target.value})} className="p-2 border rounded" rows={3} />
                                                <input type="number" placeholder="Sort Order" value={isAdding ? newItemForm.sort_order : editingItem.sort_order} onChange={(e) => isAdding ? setNewItemForm({...newItemForm, sort_order: e.target.value}) : setEditingItem({...editingItem, sort_order: e.target.value})} className="p-2 border rounded" />
                                            </div>
                                            <div className="mt-4 flex space-x-2">
                                                <button onClick={() => handleAction(isAdding ? 'add_faq' : 'edit_faq', isAdding ? newItemForm : editingItem)} className="bg-green-600 text-white px-4 py-2 rounded flex items-center"><CheckIcon className="w-4 h-4 mr-1" /> Save</button>
                                                <button onClick={() => { setIsAdding(false); setEditingItem(null); }} className="bg-gray-400 text-white px-4 py-2 rounded flex items-center"><XMarkIcon className="w-4 h-4 mr-1" /> Cancel</button>
                                            </div>
                                        </div>
                                    )}
                                    <div className="grid gap-4">
                                        {content.faq.questions.map((item: any) => (
                                            <div key={item.id} className="p-4 border rounded-xl">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <p className="font-bold text-gray-900">{item.question}</p>
                                                        <p className="text-sm text-gray-500 mt-1">{item.answer}</p>
                                                    </div>
                                                    <div className="flex space-x-1">
                                                        <button onClick={() => setEditingItem(item)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><PencilSquareIcon className="w-4 h-4" /></button>
                                                        <button onClick={() => { if(window.confirm('Delete this FAQ?')) handleAction('delete_faq', { id: item.id }); }} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><TrashIcon className="w-4 h-4" /></button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                             {/* PAGES TAB */}
                             {activeTab === 'pages' && (
                                <PagesTab
                                    pages={(content as any).pages || []}
                                    navArray={(content as any).navArray || []}
                                    isAdding={isAdding}
                                    setIsAdding={setIsAdding}
                                    newItemForm={newItemForm}
                                    setNewItemForm={setNewItemForm}
                                    handleAction={handleAction}
                                />
                            )}

                             {/* SLIDERS TAB */}
                             {activeTab === 'sliders' && (
                                <div className="space-y-6">
                                    {(isAdding || editingItem) && (
                                        <div className="bg-gray-50 p-6 rounded-xl border-2 border-[#EF5D22]/20 mb-8">
                                            <h3 className="font-bold text-lg mb-4">{isAdding ? 'Add New Slider' : 'Edit Slider'}</h3>
                                            <div className="grid grid-cols-2 gap-4">
                                                <input placeholder="Title Prefix" value={isAdding ? newItemForm.title_prefix : editingItem.title_prefix} onChange={(e) => isAdding ? setNewItemForm({...newItemForm, title_prefix: e.target.value}) : setEditingItem({...editingItem, title_prefix: e.target.value})} className="p-2 border rounded" />
                                                <input placeholder="Title Highlight" value={isAdding ? newItemForm.title_highlight : editingItem.title_highlight} onChange={(e) => isAdding ? setNewItemForm({...newItemForm, title_highlight: e.target.value}) : setEditingItem({...editingItem, title_highlight: e.target.value})} className="p-2 border rounded" />
                                                <div className="col-span-2">
                                                    <RichTextEditor
                                                        value={isAdding ? newItemForm.description : editingItem.description}
                                                        onChange={html => isAdding ? setNewItemForm({...newItemForm, description: html}) : setEditingItem({...editingItem, description: html})}
                                                        placeholder="Slider description (rich text supported)"
                                                        minHeight={80}
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <ImageUploader
                                                        label="Slider Background Image"
                                                        folder="sliders"
                                                        value={isAdding ? (newItemForm.image_url || '') : (editingItem.image_url || '')}
                                                        onChange={(path) => isAdding ? setNewItemForm({...newItemForm, image_url: path}) : setEditingItem({...editingItem, image_url: path})}
                                                    />
                                                </div>
                                                <input type="number" placeholder="Sort Order" value={isAdding ? newItemForm.sort_order : editingItem.sort_order} onChange={(e) => isAdding ? setNewItemForm({...newItemForm, sort_order: e.target.value}) : setEditingItem({...editingItem, sort_order: e.target.value})} className="p-2 border rounded" />
                                            </div>
                                            <div className="mt-4 flex space-x-2">
                                                <button onClick={() => handleAction(isAdding ? 'add_slider' : 'edit_slider', isAdding ? newItemForm : editingItem)} className="bg-green-600 text-white px-4 py-2 rounded flex items-center"><CheckIcon className="w-4 h-4 mr-1" /> Save</button>
                                                <button onClick={() => { setIsAdding(false); setEditingItem(null); }} className="bg-gray-400 text-white px-4 py-2 rounded flex items-center"><XMarkIcon className="w-4 h-4 mr-1" /> Cancel</button>
                                            </div>
                                        </div>
                                    )}
                                    <div className="grid gap-6">
                                        {((content as any).sliders as any[] || []).map((slider: any) => (
                                            <div key={slider.id} className="flex border rounded-2xl overflow-hidden hover:shadow-md transition-all">
                                                <img src={slider.image_url} alt="" className="w-40 h-auto object-cover bg-gray-100" />
                                                <div className="p-4 flex-1 flex justify-between items-center">
                                                    <div>
                                                        <p className="font-bold text-gray-900">{slider.title_prefix} {slider.title_highlight}</p>
                                                        <p className="text-xs text-gray-500 mt-1 line-clamp-1">{slider.description}</p>
                                                    </div>
                                                    <div className="flex space-x-1">
                                                        <button onClick={() => setEditingItem(slider)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><PencilSquareIcon className="w-5 h-5" /></button>
                                                        <button onClick={() => { if(window.confirm('Delete this slider?')) handleAction('delete_slider', { id: slider.id }); }} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><TrashIcon className="w-5 h-5" /></button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* FOOTER TAB */}
                            {activeTab === 'footer' && (() => {
                                const fs = (content as any).footer_settings || {};
                                const propListStr = typeof fs.propertyList === 'string'
                                    ? fs.propertyList
                                    : (fs.propertyList || []).join('|');
                                return (
                                    <div className="space-y-8">
                                        {/* Appearance */}
                                        <details className="border rounded-xl overflow-hidden group" open>
                                            <summary className="flex items-center justify-between p-5 cursor-pointer bg-gray-50 hover:bg-gray-100 font-bold text-gray-800 select-none">
                                                <span>🎨 Appearance</span>
                                                <ChevronDownIcon className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
                                            </summary>
                                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 bg-white">
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Background Color</label>
                                                    <BgColorPicker value={fs.bg_color || '#0A1D2D'} onChange={v => handleAction('update_footer', { bg_color: v })} />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Accent Color (borders, icons)</label>
                                                    <BgColorPicker value={fs.accent_color || '#EF5D22'} onChange={v => handleAction('update_footer', { accent_color: v })} />
                                                </div>
                                            </div>
                                        </details>

                                        {/* Text content */}
                                        <details className="border rounded-xl overflow-hidden group" open>
                                            <summary className="flex items-center justify-between p-5 cursor-pointer bg-gray-50 hover:bg-gray-100 font-bold text-gray-800 select-none">
                                                <span>✏️ Text Content</span>
                                                <ChevronDownIcon className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
                                            </summary>
                                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white">
                                                <div className="md:col-span-2">
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">About / Tagline Text</label>
                                                    <textarea
                                                        defaultValue={fs.aboutText || ''}
                                                        onBlur={e => handleAction('update_footer', { aboutText: e.target.value })}
                                                        rows={3} className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-[#EF5D22] outline-none"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Quick Links Column Title</label>
                                                    <input defaultValue={fs.quickLinksTitle || 'Quick Links'} onBlur={e => handleAction('update_footer', { quickLinksTitle: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Properties Column Title</label>
                                                    <input defaultValue={fs.propertiesTitle || 'Properties'} onBlur={e => handleAction('update_footer', { propertiesTitle: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Contact Info Column Title</label>
                                                    <input defaultValue={fs.contactInfoTitle || 'Contact info'} onBlur={e => handleAction('update_footer', { contactInfoTitle: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Copyright Text</label>
                                                    <input defaultValue={fs.copyright || ''} onBlur={e => handleAction('update_footer', { copyright: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Privacy Policy Label</label>
                                                    <input defaultValue={fs.privacy || 'Privacy Policy'} onBlur={e => handleAction('update_footer', { privacy: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Terms Label</label>
                                                    <input defaultValue={fs.terms || 'Terms & Conditions'} onBlur={e => handleAction('update_footer', { terms: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                                </div>
                                            </div>
                                        </details>

                                        {/* Property list */}
                                        <details className="border rounded-xl overflow-hidden group">
                                            <summary className="flex items-center justify-between p-5 cursor-pointer bg-gray-50 hover:bg-gray-100 font-bold text-gray-800 select-none">
                                                <span>🏘️ Properties List</span>
                                                <ChevronDownIcon className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
                                            </summary>
                                            <div className="p-6 bg-white">
                                                <p className="text-xs text-gray-500 mb-3">Enter each property name on a new line. They will be shown in the footer "Properties" column.</p>
                                                <textarea
                                                    defaultValue={propListStr.replace(/\|/g, '\n')}
                                                    onBlur={e => handleAction('update_footer', { propertyList: e.target.value.split('\n').filter(Boolean).join('|') })}
                                                    rows={6}
                                                    className="w-full p-2.5 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-[#EF5D22] outline-none"
                                                />
                                            </div>
                                        </details>
                                    </div>
                                );
                            })()}

                            {/* CONTACT PAGE TAB */}
                            {activeTab === 'contact_page' && (() => {
                                const cp = (content as any).contact_page || {};
                                const fields: any[] = (content as any).contact_form_fields || [];
                                return (
                                    <div className="space-y-2">
                                        <p className="text-sm text-gray-500 mb-6">Manage the Contact page appearance, text content, colours, and form fields.</p>

                                        {/* Appearance */}
                                        <details className="border rounded-xl overflow-hidden group" open>
                                            <summary className="flex items-center justify-between p-5 cursor-pointer bg-gray-50 hover:bg-gray-100 font-bold text-gray-800 select-none">
                                                <span>🎨 Colours & Layout</span>
                                                <ChevronDownIcon className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
                                            </summary>
                                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 bg-white">
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Page Background</label>
                                                    <BgColorPicker value={cp.bg_color || '#ffffff'} onChange={v => handleAction('update_contact_page', { bg_color: v })} />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Form Card Background</label>
                                                    <BgColorPicker value={cp.form_bg_color || '#f9fafb'} onChange={v => handleAction('update_contact_page', { form_bg_color: v })} />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Accent Color (badge, buttons, icons)</label>
                                                    <BgColorPicker value={cp.accent_color || '#EF5D22'} onChange={v => handleAction('update_contact_page', { accent_color: v })} />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title Font Size</label>
                                                    <select
                                                        value={cp.title_size || 'text-5xl'}
                                                        onChange={e => handleAction('update_contact_page', { title_size: e.target.value })}
                                                        className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-[#EF5D22] outline-none"
                                                    >
                                                        <option value="text-3xl">Small (text-3xl)</option>
                                                        <option value="text-4xl">Medium (text-4xl)</option>
                                                        <option value="text-5xl">Large (text-5xl) — default</option>
                                                        <option value="text-6xl">X-Large (text-6xl)</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </details>

                                        {/* Text content */}
                                        <details className="border rounded-xl overflow-hidden group">
                                            <summary className="flex items-center justify-between p-5 cursor-pointer bg-gray-50 hover:bg-gray-100 font-bold text-gray-800 select-none">
                                                <span>✏️ Page Text Content</span>
                                                <ChevronDownIcon className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
                                            </summary>
                                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white">
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Badge Text</label>
                                                    <input defaultValue={cp.badge || 'Get In Touch'} onBlur={e => handleAction('update_contact_page', { badge: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">CTA Button Label</label>
                                                    <input defaultValue={cp.ctaButton || 'Send Request'} onBlur={e => handleAction('update_contact_page', { ctaButton: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title Part 1</label>
                                                    <input defaultValue={cp.titlePart1 || 'Schedule Your'} onBlur={e => handleAction('update_contact_page', { titlePart1: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title Part 2 (Accent colour)</label>
                                                    <input defaultValue={cp.titlePart2 || 'Site Visit'} onBlur={e => handleAction('update_contact_page', { titlePart2: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Title Part 3</label>
                                                    <input defaultValue={cp.titlePart3 || 'Today'} onBlur={e => handleAction('update_contact_page', { titlePart3: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Description Paragraph</label>
                                                    <RichTextEditor
                                                        value={cp.description || ''}
                                                        onChange={v => handleAction('update_contact_page', { description: v })}
                                                        placeholder="Contact page description…"
                                                        minHeight={80}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Office Info Title</label>
                                                    <input defaultValue={cp.officeTitle || 'Visit Our Office'} onBlur={e => handleAction('update_contact_page', { officeTitle: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Call Info Title</label>
                                                    <input defaultValue={cp.callTitle || 'Call Us Directly'} onBlur={e => handleAction('update_contact_page', { callTitle: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Call Hours</label>
                                                    <input defaultValue={cp.callHours || 'Mon-Sun: 9:00 AM - 7:00 PM'} onBlur={e => handleAction('update_contact_page', { callHours: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Email Info Title</label>
                                                    <input defaultValue={cp.emailTitle || 'Send Us An Email'} onBlur={e => handleAction('update_contact_page', { emailTitle: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Email Response Note</label>
                                                    <input defaultValue={cp.emailResponse || "We'll respond within 24 hours."} onBlur={e => handleAction('update_contact_page', { emailResponse: e.target.value })} className="w-full p-2.5 border rounded-lg" />
                                                </div>
                                            </div>
                                        </details>

                                        {/* Google Map */}
                                        <details className="border rounded-xl overflow-hidden group">
                                            <summary className="flex items-center justify-between p-5 cursor-pointer bg-gray-50 hover:bg-gray-100 font-bold text-gray-800 select-none">
                                                <span>🗺️ Google Map Location</span>
                                                <ChevronDownIcon className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
                                            </summary>
                                            <div className="p-6 bg-white space-y-4">
                                                <p className="text-sm text-gray-500">
                                                    Paste a Google Maps <strong>embed URL</strong> to display a live map on the contact page.
                                                    Go to <a href="https://maps.google.com" target="_blank" rel="noreferrer noopener" className="text-[#EF5D22] underline">Google Maps</a> → find your location →
                                                    Share → Embed a map → copy the <code className="bg-gray-100 px-1 rounded text-xs">src="…"</code> value.
                                                </p>
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Map Embed URL</label>
                                                    <input
                                                        defaultValue={cp.map_embed || ''}
                                                        onBlur={e => handleAction('update_contact_page', { map_embed: e.target.value })}
                                                        placeholder="https://www.google.com/maps/embed?pb=…"
                                                        className="w-full p-2.5 border rounded-lg text-sm font-mono"
                                                    />
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer font-medium">
                                                        <input
                                                            type="checkbox"
                                                            defaultChecked={cp.show_map !== '0'}
                                                            onChange={e => handleAction('update_contact_page', { show_map: e.target.checked ? '1' : '0' })}
                                                            className="w-4 h-4 accent-[#EF5D22]"
                                                        />
                                                        Show map on contact page
                                                    </label>
                                                </div>
                                                {cp.map_embed && (
                                                    <div className="mt-3 rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                                                        <iframe
                                                            src={cp.map_embed}
                                                            width="100%"
                                                            height="260"
                                                            style={{ border: 0 }}
                                                            allowFullScreen
                                                            loading="lazy"
                                                            referrerPolicy="no-referrer-when-downgrade"
                                                            title="Map preview"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </details>

                                        {/* Form fields */}
                                        <details className="border rounded-xl overflow-hidden group" open>
                                            <summary className="flex items-center justify-between p-5 cursor-pointer bg-gray-50 hover:bg-gray-100 font-bold text-gray-800 select-none">
                                                <span>📝 Contact Form Fields</span>
                                                <ChevronDownIcon className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
                                            </summary>
                                            <div className="p-6 bg-white space-y-4">
                                                {/* Existing fields */}
                                                {fields.map((field, idx) => (
                                                    <div key={field.id} className="border rounded-xl p-4 bg-gray-50 space-y-3">
                                                        <div className="flex justify-between items-center">
                                                            <span className="font-bold text-gray-700 text-sm">Field #{idx + 1}: <span className="text-[#EF5D22]">{field.label}</span></span>
                                                            <button title="Delete" onClick={() => { if(window.confirm('Delete this field?')) handleAction('delete_form_field', { id: field.id }); }} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"><TrashIcon className="w-4 h-4" /></button>
                                                        </div>
                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                            <div>
                                                                <label className="text-xs text-gray-500 uppercase font-bold block mb-1">Type</label>
                                                                <select
                                                                    value={field.field_type}
                                                                    onChange={e => handleAction('edit_form_field', { ...field, field_type: e.target.value })}
                                                                    className="w-full p-2 border rounded-lg text-sm"
                                                                >
                                                                    <option value="text">Text</option>
                                                                    <option value="tel">Phone</option>
                                                                    <option value="email">Email</option>
                                                                    <option value="textarea">Textarea</option>
                                                                    <option value="select">Dropdown</option>
                                                                </select>
                                                            </div>
                                                            <div>
                                                                <label className="text-xs text-gray-500 uppercase font-bold block mb-1">Label</label>
                                                                <input
                                                                    defaultValue={field.label}
                                                                    onBlur={e => handleAction('edit_form_field', { ...field, label: e.target.value })}
                                                                    className="w-full p-2 border rounded-lg text-sm"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="text-xs text-gray-500 uppercase font-bold block mb-1">Placeholder</label>
                                                                <input
                                                                    defaultValue={field.placeholder || ''}
                                                                    onBlur={e => handleAction('edit_form_field', { ...field, placeholder: e.target.value })}
                                                                    className="w-full p-2 border rounded-lg text-sm"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="text-xs text-gray-500 uppercase font-bold block mb-1">Width</label>
                                                                <select
                                                                    value={String(field.col_span)}
                                                                    onChange={e => handleAction('edit_form_field', { ...field, col_span: parseInt(e.target.value) })}
                                                                    className="w-full p-2 border rounded-lg text-sm"
                                                                >
                                                                    <option value="1">Half</option>
                                                                    <option value="2">Full</option>
                                                                </select>
                                                            </div>
                                                        </div>
                                                        {(field.field_type === 'select') && (
                                                            <div>
                                                                <label className="text-xs text-gray-500 uppercase font-bold block mb-1">Options (pipe-separated: Option 1|Option 2)</label>
                                                                <input
                                                                    defaultValue={field.options || ''}
                                                                    onBlur={e => handleAction('edit_form_field', { ...field, options: e.target.value })}
                                                                    className="w-full p-2 border rounded-lg text-sm font-mono"
                                                                    placeholder="Morning (9AM-12PM)|Afternoon|Evening"
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="flex items-center gap-3">
                                                            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!!field.is_required}
                                                                    onChange={e => handleAction('edit_form_field', { ...field, is_required: e.target.checked ? 1 : 0 })}
                                                                    className="w-4 h-4 accent-[#EF5D22]"
                                                                />
                                                                Required
                                                            </label>
                                                            <div className="flex items-center gap-2 ml-auto">
                                                                <label className="text-xs text-gray-500 uppercase font-bold">Sort Order</label>
                                                                <input
                                                                    type="number"
                                                                    defaultValue={field.sort_order}
                                                                    onBlur={e => handleAction('edit_form_field', { ...field, sort_order: parseInt(e.target.value) })}
                                                                    className="w-16 p-1.5 border rounded-lg text-sm text-center"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}

                                                {/* Add new field */}
                                                <AddFormFieldPanel onAdd={(f) => handleAction('add_form_field', f)} />
                                            </div>
                                        </details>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Admin;
